import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { formatDate, formatDateTime } from '../utils/formatDate';

const GET_PERSONAL_REPORTE = gql`
  query GetPersonalParaCartelera {
    personal_fiscalia {
      id
      nombre_completo
      fecha_nacimiento
      cargo
      tipo_personal
      componente
      grado
      ubicacion
    }
  }
`;

const GET_ENVIOS_REPORTE = gql`
  query GetEnviosReporte($where: cola_envios_bool_exp) {
    cola_envios(where: $where, order_by: { created_at: desc }) {
      id
      canal
      estado
      destinatario_email
      destinatario_telefono
      asunto
      created_at
      programado_para
    }
  }
`;

const ReportesPersonal = ({ onVolver }) => {
  const [vista, setVista] = useState('menu');
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [filtroTipo, setFiltroTipo] = useState('TODOS');
  const [filtroUbicacion, setFiltroUbicacion] = useState('TODOS');
  const [canalEnvio, setCanalEnvio] = useState('TODOS');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  
  const { data, loading } = useQuery(GET_PERSONAL_REPORTE);
  const whereEnvios = useMemo(() => {
    const where = {};
    if (canalEnvio !== 'TODOS') {
      where.canal = { _eq: canalEnvio.toLowerCase() };
    }
    if (fechaDesde || fechaHasta) {
      const range = {};
      if (fechaDesde) range._gte = `${fechaDesde}T00:00:00Z`;
      if (fechaHasta) range._lte = `${fechaHasta}T23:59:59Z`;
      where.programado_para = range;
    }
    return where;
  }, [canalEnvio, fechaDesde, fechaHasta]);

  const { data: dataEnvios, loading: loadingEnvios } = useQuery(GET_ENVIOS_REPORTE, {
    variables: { where: whereEnvios },
    fetchPolicy: 'network-only',
  });

  const handleBack = () => {
    if (vista !== 'menu') {
      setVista('menu');
      return;
    }
    onVolver();
  };

  const meses = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const personal = data?.personal_fiscalia || [];
  const envios = dataEnvios?.cola_envios || [];

  // Obtener ubicaciones únicas para el filtro
  const ubicacionesUnicas = [...new Set(personal.map(p => p.ubicacion))].filter(u => u).sort();

  // Filtrado dinámico
  const listadoFiltrado = personal.filter(p => {
    const cumpleMes = (new Date(p.fecha_nacimiento).getUTCMonth() + 1) === parseInt(mes);
    const cumpleTipo = filtroTipo === 'TODOS' || p.tipo_personal === filtroTipo;
    const cumpleUbicacion = filtroUbicacion === 'TODOS' || p.ubicacion === filtroUbicacion;
    return cumpleMes && cumpleTipo && cumpleUbicacion;
  });

  return (
    <div className="bg-light min-vh-100 pb-5">
      <div 
        className="bg-white border-bottom d-flex align-items-center sticky-top shadow-sm d-print-none px-3 py-3"
        onClick={handleBack}
        style={{cursor: 'pointer'}}
      >
        <i className="bi bi-arrow-left fs-3 me-3 text-primary"></i>
        <h5 className="mb-0 fw-bold">Reportes Comunicacional</h5>
      </div>

      <div className="container mt-4 px-3">
        {vista === 'menu' && (
          <div className="card border-0 shadow-sm p-4 mb-4" style={{borderRadius: '15px'}}>
            <h6 className="text-muted small fw-bold mb-3 uppercase">SELECCIONA UN REPORTE</h6>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <button className="btn btn-primary w-100 py-3 fw-bold shadow-sm" onClick={() => setVista('cumple')}>
                  <i className="bi bi-balloon me-2"></i>CUMPLEAÑEROS
                </button>
              </div>
              <div className="col-12 col-md-6">
                <button className="btn btn-primary w-100 py-3 fw-bold shadow-sm" onClick={() => setVista('envios')}>
                  <i className="bi bi-send me-2"></i>ENVIOS MASIVOS
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Vista de Cartelera (Lo que se imprime) */}
        {vista === 'cumple' && (
          <>
            <div className="card border-0 shadow-sm p-4 mb-4 d-print-none" style={{borderRadius: '15px'}}>
              <h6 className="text-muted small fw-bold mb-3 uppercase">CONFIGURACIÓN DE CUMPLEAÑEROS</h6>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label small">Mes</label>
                  <select className="form-select" value={mes} onChange={(e) => setMes(e.target.value)}>
                    {meses.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Ubicación / Región</label>
                  <select className="form-select" value={filtroUbicacion} onChange={(e) => setFiltroUbicacion(e.target.value)}>
                    <option value="TODOS">Todas las sedes</option>
                    {ubicacionesUnicas.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Clasificación</label>
                  <select className="form-select" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                    <option value="TODOS">Civil y Militar</option>
                    <option value="MILITAR">Solo Militar</option>
                    <option value="CIVIL">Solo Civil</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <button className="btn btn-primary w-100 py-2 fw-bold shadow-sm" onClick={() => window.print()}>
                  <i className="bi bi-printer me-2"></i>IMPRIMIR CUMPLEAÑEROS
                </button>
              </div>
            </div>
            <div className="cartelera-box cumple-section bg-white shadow p-5" style={{borderRadius: '20px', minHeight: '800px', border: '2px solid #eee'}}>
          <div className="text-center mb-5 border-bottom pb-4">
            <h1 className="fw-bold text-dark mb-1">🎂 REPORTE DE CUMPLEAÑEROS 🎂</h1>
            <h3 className="text-primary text-uppercase">{meses[mes-1]}</h3>
            <div className="badge bg-secondary p-2 mt-2">{filtroUbicacion === 'TODOS' ? 'NIVEL NACIONAL' : filtroUbicacion}</div>
          </div>

          {listadoFiltrado.length > 0 ? (
            <div className="row g-4">
              {listadoFiltrado.sort((a,b) => new Date(a.fecha_nacimiento).getUTCDate() - new Date(b.fecha_nacimiento).getUTCDate()).map(p => (
                <div key={p.id} className="col-md-6 col-lg-4">
                  <div className="p-3 border rounded-3 text-center h-100 shadow-sm" style={{backgroundColor: '#fffcf2'}}>
                    <div className="display-6 fw-bold text-warning mb-1">
                      {formatDate(p.fecha_nacimiento)}
                    </div>
                    <div className="fw-bold text-dark text-uppercase" style={{fontSize: '1.1rem'}}>{p.nombre_completo}</div>
                    <div className="text-muted small mt-1">{p.cargo}</div>
                    {p.tipo_personal === 'MILITAR' && (
                        <div className="badge bg-primary bg-opacity-10 text-primary mt-2">{p.grado} - {p.componente}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
                <i className="bi bi-calendar-x fs-1 d-block mb-3"></i>
                No hay cumpleaños registrados para este mes y ubicación.
            </div>
          )}

          <div className="mt-5 pt-5 text-center text-muted small border-top">
            Fiscalía General Militar - Sistema Icarosoft Legal
          </div>
            </div>
          </>
        )}

        {/* Reporte de envios masivos */}
        {vista === 'envios' && (
          <div className="card border-0 shadow-sm p-4 mt-4 envios-section" style={{borderRadius: '15px'}}>
            <h6 className="text-muted small fw-bold mb-3 uppercase">REPORTE DE ENVIOS MASIVOS</h6>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label small">Canal</label>
                <select className="form-select" value={canalEnvio} onChange={(e) => setCanalEnvio(e.target.value)}>
                  <option value="TODOS">Todos</option>
                  <option value="EMAIL">Email</option>
                  <option value="SMS">SMS</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label small">Desde</label>
                  <input
                    className="form-control"
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                  />
              </div>
              <div className="col-md-4">
                <label className="form-label small">Hasta</label>
                  <input
                    className="form-control"
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                  />
              </div>
            </div>
            <div className="mt-4 d-print-none">
              <button className="btn btn-primary w-100 py-2 fw-bold shadow-sm" onClick={() => window.print()}>
                <i className="bi bi-printer me-2"></i>IMPRIMIR ENVIOS MASIVOS
              </button>
            </div>

            <div className="table-responsive mt-4">
              <table className="table table-sm table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Canal</th>
                    <th>Estado</th>
                    <th>Destino</th>
                    <th>Asunto</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingEnvios && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">Cargando envios...</td>
                    </tr>
                  )}
                  {!loadingEnvios && envios.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">Sin envios para el filtro seleccionado</td>
                    </tr>
                  )}
                  {envios.map(e => (
                    <tr key={e.id}>
                      <td>{formatDateTime(e.programado_para)}</td>
                      <td>{(e.canal || '').toUpperCase()}</td>
                      <td>{e.estado}</td>
                      <td>{e.destinatario_email || e.destinatario_telefono || '—'}</td>
                      <td>{e.asunto || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
      
      <style>{`
        @media print {
          body { background: white !important; }
          .bg-light { background: white !important; }
          .cartelera-box { box-shadow: none !important; border: none !important; padding: 0 !important; }
          .btn, .d-print-none { display: none !important; }
          @page { margin: 1cm; }
        }
      `}</style>
    </div>
  );
};

export default ReportesPersonal;
